import { Link } from "@inertiajs/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import ContentLayout from "../shared/ContentLayout";
import PageHeader from "../shared/PageHeader";

interface Attachment {
  id: number
  title: string | null
  description: string | null
  public_url: string
  thumbnail_url: string | null
  filename?: string
  created_at: string
}

interface AttachmentInfo {
  id: number
  title: string | null
}

interface GroupInfo {
  id: number
  title: string
}

export default function MediaAttachmentShow({
  attachment,
  group,
  nextAttachment,
  previousAttachment,
}: {
  attachment: Attachment
  group: GroupInfo
  nextAttachment: AttachmentInfo | null
  previousAttachment: AttachmentInfo | null
}) {
  const displayTitle = attachment.title || attachment.filename || 'Untitled'

  const renderBreadcrumbs = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <div className="breadcrumbs text-sm">
        <ul>
          <li>
            <Link
              href="/"
              className="link link-primary"
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              href="/media"
              className="link link-primary"
            >
              Media
            </Link>
          </li>
          <li>
            <Link
              href={`/media/${group.id}`}
              className="link link-primary"
            >
              {group.title}
            </Link>
          </li>
          <li>
            <span>{displayTitle}</span>
          </li>
        </ul>
      </div>

      <div style={{
        display: 'flex',
        gap: '8px',
      }}>
        {previousAttachment && (
          <Link
            href={`/media/${group.id}/attachments/${previousAttachment.id}`}
            className="btn btn-outline btn-primary btn-xs"
            title={previousAttachment.title || 'Previous'}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Link>
        )}
        {nextAttachment && (
          <Link
            href={`/media/${group.id}/attachments/${nextAttachment.id}`}
            className="btn btn-outline btn-primary btn-xs"
            title={nextAttachment.title || 'Next'}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Link>
        )}
      </div>
    </div>
  );

  const renderFooterNavigation = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        {previousAttachment && (
          <Link
            href={`/media/${group.id}/attachments/${previousAttachment.id}`}
            className="btn btn-outline btn-primary w-full justify-start"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}>
              <div className="truncate">{previousAttachment.title || 'Previous'}</div>
            </div>
          </Link>
        )}
      </div>

      <div className="flex items-center justify-center">
        <Link
          href={`/media/${group.id}`}
          className="btn btn-primary btn-sm"
        >
          View All in {group.title}
        </Link>
      </div>

      <div>
        {nextAttachment && (
          <Link
            href={`/media/${group.id}/attachments/${nextAttachment.id}`}
            className="btn btn-outline btn-primary w-full justify-end"
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}>
              <div className="truncate">{nextAttachment.title || 'Next'}</div>
            </div>
            <FontAwesomeIcon icon={faChevronRight} />
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <ContentLayout
      breadcrumbs={renderBreadcrumbs()}
      footerNavigation={renderFooterNavigation()}
    >
      <div className="p-3 sm:p-4 max-w-4xl">
        <PageHeader title={displayTitle} description={attachment.description || undefined} />
        <div className="flex justify-center mb-4">
          <img
            src={attachment.public_url}
            alt={displayTitle}
            className="max-w-full h-auto border border-gray-200"
          />
        </div>
        {attachment.filename && (
          <div className="p-3 bg-gray-100">
            <p className="text-sm text-gray-600">
              <strong>File:</strong> {attachment.filename}
            </p>
          </div>
        )}
      </div>
    </ContentLayout>
  );
}
